/**
 * Suivi des justificatifs des opérations bancaires.
 *
 * Moteur pur : il dit, pour chaque ligne du compte courant, si une pièce la
 * justifie, si une règle de dispense validée l'en dispense, ou si elle reste à
 * justifier ; puis il résume l'exercice par état, par mois et par
 * rapprochement. Conception : `docs/superpowers/specs/2026-11-02-justificatifs-tableau-de-bord-design.md`.
 */
import { regleCorrespond, sensEcriture } from './classement.js';
export const ETATS_JUSTIFICATIF = [
    'a_justifier',
    'a_categoriser',
    'non_evalue',
    'dispense',
    'facture',
    'justifie',
];
/**
 * Règles amorcées au statut `proposee` : les dispenses se décident ensemble
 * (demande de Yannis Jean, 2 novembre 2026), aucune n'agit avant validation.
 */
export const REGLES_DISPENSE_PROPOSEES = [
    {
        champ: 'compte_libelle', operateur: 'contient', valeur: 'frais bancaires', sens: 'depense', montantMax: null,
        motif: 'Frais prélevés par la banque : le relevé fait foi.',
    },
    {
        champ: 'type_transaction', operateur: 'egal', valeur: 'transfer_fund', sens: 'tous', montantMax: null,
        motif: 'Mouvement de fonds ou prêt : pièce interne.',
    },
    {
        champ: 'type_transaction', operateur: 'egal', valeur: 'expense_refund', sens: 'recette', montantMax: null,
        motif: 'Remboursement d’une dépense déjà justifiée.',
    },
    {
        champ: 'tiers', operateur: 'contient', valeur: 'fcap', sens: 'recette', montantMax: null,
        motif: 'Versement des fonds FCAP : avis de versement de l’organisme.',
    },
];
/** La règle correspond-elle à l'écriture ? Le statut n'est pas regardé (aperçu d'une règle proposée). */
export function regleDispenseCorrespond(r, e) {
    if (r.montantMax !== null && e.montant > r.montantMax)
        return false;
    return regleCorrespond({ ...r, priorite: 0, sousPoste: '', actif: true }, e);
}
export function etatJustificatif(op, regles) {
    if (!op.categorisee)
        return { etat: 'a_categoriser', regleId: null };
    if (op.piece === 'jointe')
        return { etat: 'justifie', regleId: null };
    if (op.piece === 'facture')
        return { etat: 'facture', regleId: null };
    const r = [...regles]
        .sort((a, b) => a.id - b.id)
        .find((x) => x.statut === 'active' && regleDispenseCorrespond(x, op.ecriture));
    if (r)
        return { etat: 'dispense', regleId: r.id };
    return { etat: op.piece === 'absente' ? 'a_justifier' : 'non_evalue', regleId: null };
}
function compteursVides() {
    return Object.fromEntries(ETATS_JUSTIFICATIF.map((x) => [x, { nb: 0, montant: 0 }]));
}
function arrondir(c) {
    return Object.fromEntries(Object.entries(c).map(([k, v]) => [k, { nb: v.nb, montant: Math.round(v.montant * 100) / 100 }]));
}
export function syntheseJustificatifs(ops, regles, exercice) {
    const prefixe = `${String(exercice)}-`;
    const tous = compteursVides();
    const recettes = compteursVides();
    const depenses = compteursVides();
    const parMois = new Map();
    for (const op of ops) {
        if (!op.ecriture.date.startsWith(prefixe))
            continue;
        const { etat } = etatJustificatif(op, regles);
        const m = op.ecriture.montant;
        const mois = op.ecriture.date.slice(0, 7);
        const pm = parMois.get(mois) ?? { c: compteursVides(), lignes: 0, rapprochees: 0 };
        parMois.set(mois, pm);
        for (const c of [tous, sensEcriture(op.ecriture) === 'recette' ? recettes : depenses, pm.c]) {
            c[etat].nb += 1;
            c[etat].montant += m;
        }
        pm.lignes += 1;
        if (op.rapproche)
            pm.rapprochees += 1;
    }
    const mois = [...parMois.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([cle, v]) => ({
        mois: cle,
        lignes: v.lignes,
        parEtat: arrondir(v.c),
        rapprochees: v.rapprochees,
        rapproche: v.rapprochees === v.lignes,
    }));
    let dernierMoisRapproche = null;
    for (const m of mois) {
        if (!m.rapproche)
            break;
        dernierMoisRapproche = m.mois;
    }
    const sansSuite = tous.justifie.nb + tous.facture.nb + tous.dispense.nb;
    const evaluees = sansSuite + tous.a_justifier.nb;
    return {
        exercice,
        parEtat: arrondir(tous),
        recettes: arrondir(recettes),
        depenses: arrondir(depenses),
        mois,
        tauxJustifie: evaluees === 0 ? null : sansSuite / evaluees,
        dernierMoisRapproche,
        moisNonRapproches: mois.filter((m) => !m.rapproche).length,
    };
}
export function pointDeSuivi(s, date) {
    return {
        date,
        aJustifier: s.parEtat.a_justifier,
        aCategoriser: s.parEtat.a_categoriser,
        dispense: s.parEtat.dispense,
        justifie: s.parEtat.justifie.nb + s.parEtat.facture.nb,
        moisNonRapproches: s.moisNonRapproches,
    };
}
/** Lundi de la semaine d'une date `AAAA-MM-JJ`. */
export function lundiDe(date) {
    const d = new Date(`${date}T00:00:00Z`);
    const decalage = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() - decalage);
    return d.toISOString().slice(0, 10);
}
/**
 * Dernier point de chaque semaine, pour les `semaines` dernières semaines
 * jusqu'à celle d'`aujourdhui` comprise, dans l'ordre chronologique.
 */
export function suiviHebdomadaire(points, semaines, aujourdhui) {
    const debut = new Date(`${lundiDe(aujourdhui)}T00:00:00Z`);
    debut.setUTCDate(debut.getUTCDate() - 7 * (semaines - 1));
    const borne = debut.toISOString().slice(0, 10);
    const parSemaine = new Map();
    for (const p of points) {
        if (p.date < borne || p.date > aujourdhui)
            continue;
        const cle = lundiDe(p.date);
        const deja = parSemaine.get(cle);
        if (!deja || p.date > deja.date)
            parSemaine.set(cle, p);
    }
    return [...parSemaine.values()].sort((a, b) => a.date.localeCompare(b.date));
}
/**
 * Lit le réglage `titulaires_cartes` : `4179:Yannis Jean,9660:Ariane Pouget`.
 * Une entrée mal formée est ignorée ; pour un même numéro, la première l'emporte.
 */
export function lireTitulairesCartes(texte) {
    const vus = new Set();
    const sortie = [];
    for (const brut of texte.split(',')) {
        const m = /^\s*(\d{4})\s*:\s*(.+?)\s*$/.exec(brut);
        if (!m?.[1] || !m[2] || vus.has(m[1]))
            continue;
        vus.add(m[1]);
        sortie.push({ carte: m[1], nom: m[2] });
    }
    return sortie;
}
/**
 * Quatre derniers chiffres de la carte d'un libellé du Crédit Mutuel
 * (« PAIEMENT CB 2910 RONCHIN LEROY MERLIN FR CARTE 4179 ») ; `null` sans carte.
 */
export function carteDuLibelle(libelle) {
    return /\bCARTE\s+(\d{4})\b/i.exec(libelle)?.[1] ?? null;
}
/** Carte et titulaire d'une ligne ; `nom` nul pour une carte inconnue du réglage. */
export function titulaireDe(libelle, titulaires) {
    const carte = carteDuLibelle(libelle);
    if (carte === null)
        return null;
    return { carte, nom: titulaires.find((t) => t.carte === carte)?.nom ?? null };
}
/**
 * Paiements par carte de l'exercice qui attendent une pièce (à justifier) ou
 * une catégorisation dans Zoho, regroupés par carte : les titulaires connus
 * dans l'ordre du réglage, puis les cartes inconnues par numéro.
 */
export function demandesParCarte(ops, regles, exercice, titulaires) {
    const prefixe = `${String(exercice)}-`;
    const parCarte = new Map();
    for (const op of ops) {
        if (!op.ecriture.date.startsWith(prefixe))
            continue;
        const carte = carteDuLibelle(op.ecriture.libelle);
        if (carte === null)
            continue;
        const { etat } = etatJustificatif(op, regles);
        if (etat !== 'a_justifier' && etat !== 'a_categoriser')
            continue;
        const c = parCarte.get(carte) ?? { aJustifier: { nb: 0, montant: 0 }, aCategoriser: { nb: 0, montant: 0 } };
        parCarte.set(carte, c);
        const cible = etat === 'a_justifier' ? c.aJustifier : c.aCategoriser;
        cible.nb += 1;
        cible.montant += op.ecriture.montant;
    }
    const rang = (carte) => {
        const i = titulaires.findIndex((t) => t.carte === carte);
        return i === -1 ? titulaires.length : i;
    };
    const centimes = (x) => ({ nb: x.nb, montant: Math.round(x.montant * 100) / 100 });
    return [...parCarte.entries()]
        .sort(([a], [b]) => rang(a) - rang(b) || a.localeCompare(b))
        .map(([carte, c]) => ({
        carte,
        nom: titulaires.find((t) => t.carte === carte)?.nom ?? null,
        aJustifier: centimes(c.aJustifier),
        aCategoriser: centimes(c.aCategoriser),
    }));
}
//# sourceMappingURL=justificatifs.js.map